// ***************************************************************************************
// Shuffle - Hunt mode's way out of a dead board.
//
// IT MUST BE DETERMINISTIC. A shuffle is part of the move log, and the Worker replays the
// log to score a submission; if the browser and the Worker shuffled differently the boards
// would diverge on the very next word and every honest score would be rejected.
//
// So it is not random at play time. It is a permutation derived from the puzzle's own seed
// and HOW MANY SHUFFLES HAVE ALREADY HAPPENED - both of which the Worker knows from the
// log - using the same PCG32 the board is built with.
//
// It permutes the LETTERS among the cells that are already occupied. It does not move
// tiles into empty cells, so the board's shape is untouched and nothing needs to settle
// afterwards: a shuffled board is still packed exactly as the collapse left it.
// ***************************************************************************************

import { Pcg32, GAME_RNG_STREAM } from './rng.ts';
import type { Board } from './settle.ts';

/** Hunt allows three. Exposed so the UI and the verifier read the same number. */
export const SHUFFLES_ALLOWED = 3;

/**
 * The seed for the Nth shuffle of a puzzle.
 *
 * Mixed rather than simply added, so consecutive shuffles of one board do not produce
 * near-identical permutations.
 */
export function shuffleSeed(puzzleSeed: number, ordinal: number): number {
  let h = (puzzleSeed ^ Math.imul(ordinal + 1, 0x9e3779b9)) >>> 0;

  h = (h ^ (h >>> 16)) >>> 0;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;

  return h === 0 ? 1 : h;
}

/**
 * A new board with the same cells occupied and the letters rearranged among them.
 *
 * Returns a copy; the caller's board is untouched.
 */
export function shuffleBoard(
  board: Board,
  puzzleSeed: number,
  ordinal: number,
): Board {
  const occupied: number[] = [];

  for (let at = 0; at < board.length; at++) {
    if (board[at] != null) occupied.push(at);
  }

  const next = board.slice();

  // Nothing to rearrange. Not an error - a one-tile board is a legal thing to shuffle,
  // it just cannot change.
  if (occupied.length < 2) return next;

  const letters = occupied.map((at) => board[at]!);
  const rng = new Pcg32(shuffleSeed(puzzleSeed, ordinal), GAME_RNG_STREAM);

  // Fisher-Yates, the same shape used everywhere else in the engine.
  for (let i = letters.length; i > 1; i--) {
    const j = rng.bounded(i);
    const t = letters[i - 1]!;

    letters[i - 1] = letters[j]!;
    letters[j] = t;
  }

  for (let i = 0; i < occupied.length; i++) next[occupied[i]!] = letters[i]!;

  return next;
}
